/**
 * Server-owned markers written by cancelClass.mjs after Kim cancels a class,
 * and flipped back by reinstateClass.mjs when somebody undoes the cancel.
 *
 * The booking itself is deleted, so the absence of a reservation is not proof
 * that the recurring class should be synthesized back onto /teach.  This
 * week-scoped listener is the durable negative fact that keeps a cancelled
 * session cancelled on that screen too.
 *
 * Whether a record means "did not meet" is decided in ONE place,
 * cancelledAttendance.ts isCancellationActive, for every screen and job.
 */
import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
  type FieldValue,
  type FirestoreError,
  type Query,
  type QueryDocumentSnapshot,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  activeCancelledDayKeys,
  isCancellationActive,
  markCounts,
  type ClassCancellationState,
} from './cancelledAttendance.ts';

export interface ClassCancellationHistoryEntry {
  action: 'cancelled' | 'reinstated' | 'migrated';
  by?: string;
  at?: Timestamp;
  recordOnly?: boolean;
}

export interface ClassCancellationDoc {
  id: string;
  offeringId: string;
  /** Studio-local YYYY-MM-DD, the same key cancelClass receives. */
  date: string;
  /** Immutable first-cancel time within a generation. */
  cancelledAt?: Timestamp | FieldValue;
  /** 'reinstated' once the cancel was undone. Missing on a record from before 2026-10-23. */
  state?: ClassCancellationState;
  cancelledBy?: string;
  /** True when the class was recorded as cancelled after the fact, with nobody told. */
  recordOnly?: boolean;
  reinstatedAt?: Timestamp | FieldValue;
  reinstatedBy?: string;
  history?: ClassCancellationHistoryEntry[];
  /** Increments when a reinstated class is cancelled again. */
  generation?: number;
  claimedAt?: Timestamp | FieldValue;
  claimedBy?: string;
  writesAppliedAt?: Timestamp | FieldValue;
  notifySentAt?: Timestamp | FieldValue;
  notifyCounts?: { email?: number; sms?: number; push?: number };
}

export interface UseWeekClassCancellationsResult {
  cancellations: ClassCancellationDoc[];
  loading: boolean;
  error: string | null;
  /** True until Firestore confirms the result from the server. */
  fromCache: boolean;
}

/**
 * Subscribe only to cancellation markers inside the visible Teach week.
 * One indexed date-range query replaces a listener per class and also catches
 * a class that was moved off its usual weekday before it was cancelled.
 */
export function useWeekClassCancellations(
  weekStartKey: string,
  weekEndKey: string,
  enabled = true
): UseWeekClassCancellationsResult {
  const [cancellations, setCancellations] = useState<ClassCancellationDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(true);

  useEffect(() => {
    if (!db || !enabled || !weekStartKey || !weekEndKey) {
      setCancellations([]);
      setLoading(false);
      setError(null);
      setFromCache(false);
      return;
    }

    setLoading(true);
    setError(null);
    setFromCache(true);
    const q = query(
      collection(db, 'classCancellations'),
      where('date', '>=', weekStartKey),
      where('date', '<=', weekEndKey),
      orderBy('date')
    );
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        // Only the cancels that stand. An undone one is no cancel at all.
        setCancellations(
          snap.docs
            .map((d) => ({
              id: d.id,
              ...(d.data() as Omit<ClassCancellationDoc, 'id'>),
            }))
            .filter((c) => isCancellationActive(c))
        );
        setError(null);
        setFromCache(snap.metadata.fromCache);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [enabled, weekEndKey, weekStartKey]);

  return { cancellations, loading, error, fromCache };
}

export interface UseClassDayCancellationResult {
  /** The record for this class and day, cancelled or reinstated, or null for none. */
  cancellation: ClassCancellationDoc | null;
  /** True when the record says the class did not meet. */
  cancelled: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * ONE class on ONE day, live. The Attendance page listens to this so a tab
 * opened before somebody recorded the class as cancelled stops offering
 * chips the moment the record lands, and offers them again the moment
 * somebody undoes it. One document read.
 */
export function useClassDayCancellation(
  offeringId: string | null | undefined,
  date: string | null | undefined,
  enabled = true
): UseClassDayCancellationResult {
  const [cancellation, setCancellation] = useState<ClassCancellationDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !enabled || !offeringId || !date) {
      setCancellation(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const unsub = onSnapshot(
      doc(db, 'classCancellations', `${offeringId}_${date}`),
      (snap) => {
        setCancellation(snap.exists()
          ? { id: snap.id, ...(snap.data() as Omit<ClassCancellationDoc, 'id'>) }
          : null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [enabled, offeringId, date]);

  return { cancellation, cancelled: isCancellationActive(cancellation), loading, error };
}

/**
 * The class-days whose cancel stands, from `cutoff` (YYYY-MM-DD) on, live, for
 * a reader that turns marks into attended or missed. Calls `onChange` with
 * the keys (cancelledAttendance.ts cancelledDayKey) every time they move.
 *
 * A read that fails (a rules deploy that has not landed yet, a dropped
 * connection) answers with NO cancelled days rather than leaving the reader
 * waiting forever: counting every mark is how every report counted before
 * cancels existed, and the reader's own marks still render.
 */
export function subscribeCancelledClassDays(
  cutoff: string,
  onChange: (keys: Set<string>) => void
): () => void {
  if (!db) {
    onChange(new Set());
    return () => {};
  }
  return onSnapshot(
    query(collection(db, 'classCancellations'), where('date', '>=', cutoff)),
    (snap) => onChange(activeCancelledDayKeys(snap.docs.map((d) => d.data()))),
    () => onChange(new Set())
  );
}

/** subscribeCancelledClassDays as a hook. `keys` is null until the first answer. */
export function useCancelledClassDays(cutoff: string, enabled = true): Set<string> | null {
  const [keys, setKeys] = useState<Set<string> | null>(null);
  useEffect(() => {
    if (!enabled || !cutoff) {
      setKeys(new Set());
      return;
    }
    setKeys(null);
    return subscribeCancelledClassDays(cutoff, setKeys);
  }, [cutoff, enabled]);
  return keys;
}

/**
 * A LIVE READ OF MARKS THAT ONLY EVER SEES THE ONES THAT COUNT.
 *
 * Bobbie, 2026-10-23 (Option A of the class cancellation design): a class
 * that did not meet has no attendance, on every reader, by ONE rule
 * (cancelledAttendance.ts). This listens to `q` and to the class-days whose
 * cancel stands from `cutoff` on, and hands `next` the marks that count once
 * BOTH have answered, again every time either moves. An undo flips the record
 * and that night's marks come back here with no write to any mark.
 */
export function onCountableMarks(
  q: Query,
  cutoff: string,
  next: (snap: { docs: QueryDocumentSnapshot[] }) => void,
  error: (err: FirestoreError) => void
): () => void {
  let docs: QueryDocumentSnapshot[] | null = null;
  let cancelledDays: Set<string> | null = null;
  const render = () => {
    if (!docs || !cancelledDays) return;
    const days = cancelledDays;
    next({ docs: docs.filter((d) => markCounts(d.data() as { offeringId?: unknown; date?: unknown }, days)) });
  };
  const unsubMarks = onSnapshot(
    q,
    (snap) => {
      docs = snap.docs;
      render();
    },
    error
  );
  const unsubCancelled = subscribeCancelledClassDays(cutoff, (keys) => {
    cancelledDays = keys;
    render();
  });
  return () => {
    unsubMarks();
    unsubCancelled();
  };
}
